import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState, } from 'react';
import { CornerDownRight, GripVertical, Radio } from 'lucide-react';
import { eventLabel } from '../schema';
import { cn } from '../lib/utils';
import { FieldHelp } from './FieldHelp';
const LINE_ID = 'own-arrows';
/**
 * Where a node's own arrows are checked among the graph's cross-cutting flows.
 * Flows above the line win over the node's arrows when both hold at the same
 * moment; flows below it lose. `before` is the flow just below the line; absent
 * puts the line at the bottom. The line is dragged by its grip, or moved with
 * the up and down keys while it has focus.
 */
export function OwnArrowsPlace({ flows, before, reveal = false, onRevealed, onChange, }) {
    const found = flows.findIndex(flow => flow.id === before);
    const at = found === -1 ? flows.length : found;
    const place = (index) => onChange(flows[index]?.id);
    const listed = [
        ...flows.slice(0, at),
        { id: LINE_ID, events: [] },
        ...flows.slice(at),
    ];
    const rootRef = useRef(null);
    useEffect(() => {
        if (!reveal)
            return;
        rootRef.current?.scrollIntoView({ block: 'nearest' });
        onRevealed?.();
    }, [reveal, onRevealed]);
    // A key press moves the line in the DOM, which drops its focus.
    const lineRef = useRef(null);
    const keyMoved = useRef(false);
    useEffect(() => {
        if (keyMoved.current)
            lineRef.current?.focus();
        keyMoved.current = false;
    }, [at]);
    const onKeyDown = (event) => {
        const to = event.key === 'ArrowUp' ? at - 1 : event.key === 'ArrowDown' ? at + 1 : -1;
        if (to < 0 || to > flows.length)
            return;
        event.preventDefault();
        keyMoved.current = true;
        place(to);
    };
    // The line is draggable only while its grip is held. `dropAt` is the place
    // in the list it would land.
    const [armed, setArmed] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [dropAt, setDropAt] = useState(null);
    const endDrag = () => {
        setArmed(false);
        setDragging(false);
        setDropAt(null);
    };
    const dropProps = (index) => ({
        onDragOver: (event) => {
            if (!dragging)
                return;
            event.preventDefault();
            setDropAt(index);
        },
        onDrop: (event) => {
            event.preventDefault();
            if (dragging && dropAt !== null && dropAt !== at)
                place(dropAt);
            endDrag();
        },
    });
    const dropMarker = (index) => dragging &&
        dropAt === index &&
        index !== at && (_jsx("span", { "aria-hidden": true, className: "absolute inset-x-1 h-0.5 rounded bg-primary", style: at < index ? { bottom: -1 } : { top: -1 } }));
    return (_jsxs("div", { ref: rootRef, className: "space-y-3", children: [_jsx("h3", { className: "text-sm font-medium", children: "Transitions" }), _jsx("ul", { className: "border rounded-md divide-y", children: listed.map((flow, index) => flow.id === LINE_ID ? (_jsxs("li", { ref: lineRef, tabIndex: 0, "aria-label": `This node’s arrows, checked ${at + 1} of ${flows.length + 1}. Use the up and down keys to move.`, draggable: armed, onDragStart: event => {
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', LINE_ID);
                        setDragging(true);
                    }, onDragEnd: endDrag, onKeyDown: onKeyDown, ...dropProps(index), className: cn('group relative flex items-center gap-2 bg-muted/50 px-2 py-1.5 outline-none focus-visible:ring-2 focus-visible:ring-ring', dragging && 'opacity-40'), children: [_jsxs("span", { className: "flex h-3.5 w-3.5 flex-shrink-0 cursor-grab items-center justify-center", onPointerDown: () => {
                                setArmed(true);
                                // A release off the grip disarms the line too.
                                window.addEventListener('pointerup', () => setArmed(false), {
                                    once: true,
                                });
                            }, children: [_jsx(CornerDownRight, { className: "h-3.5 w-3.5 text-primary group-hover:hidden group-focus-visible:hidden" }), _jsx(GripVertical, { "aria-label": "Drag to reorder", className: "hidden h-3.5 w-3.5 text-muted-foreground group-hover:block group-focus-visible:block" })] }), _jsx("span", { className: "flex-1 text-xs font-medium", children: "This node\u2019s arrows" })] }, LINE_ID)) : (_jsxs("li", { ...dropProps(index), className: "relative flex items-center gap-2 px-2 py-1.5", children: [dropMarker(index), _jsx(Radio, { className: "h-3.5 w-3.5 text-primary flex-shrink-0" }), _jsx("span", { className: "truncate text-xs", children: flow.events.map(eventLabel).join(' + ') ||
                                'A flow with no events' })] }, flow.id))) }), _jsx(FieldHelp, { children: "When a cross-cutting flow and an arrow of this node hold at the same moment, the one higher in this list wins. Drag \u201CThis node\u2019s arrows\u201D to change its place. The graph\u2019s flow order is set in Graph settings." })] }));
}
//# sourceMappingURL=OwnArrowsPlace.js.map